import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as PasswordResetIcon,
  SupervisorAccount as SupervisorIcon,
  Business as PartnerIcon,
  Code as DeveloperIcon,
  Badge as MemberIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  orderBy,
  where,
  writeBatch
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword, 
  updateProfile,
  getAuth,
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { initializeApp, getApps, getApp, deleteApp } from 'firebase/app';
import app, { db, auth } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { USER_COLOR_PALETTE, defaultColorForIndex, getInitials } from '../utils/userColors';

const AppUsers = () => {
  const { isSuperAdmin, userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'Partner',
    status: 'Active',
    password: '',
    color: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  // Password reset confirmation (Super Admin): { user, sending }
  const [resetTarget, setResetTarget] = useState(null);

  // Role definitions
  const roleDefinitions = {
    SA: {
      name: 'Super Admin',
      description: 'Full system access with all permissions',
      permissions: ['Create Users', 'Edit Users', 'Delete Users', 'System Config', 'All Modules'],
      color: 'error',
      icon: <SupervisorIcon />
    },
    Developer: {
      name: 'Developer',
      description: 'Super Admin access, hidden from people lists (tasks, leads, etc.)',
      permissions: ['Create Users', 'Edit Users', 'Delete Users', 'System Config', 'All Modules'],
      color: 'secondary',
      icon: <DeveloperIcon />
    },
    Partner: {
      name: 'Partner',
      description: 'Limited access to business operations',
      permissions: ['View Clients', 'Manage Proposals', 'View Reports', 'Limited Finance'],
      color: 'primary',
      icon: <PartnerIcon />
    },
    Member: {
      name: 'Member',
      description: 'Same access as Partner, without the Finance section',
      permissions: ['View Clients', 'Manage Proposals', 'View Reports'],
      color: 'info',
      icon: <MemberIcon />
    }
  };

  // Redirect if not Super Admin
  useEffect(() => {
    if (!isSuperAdmin()) {
      // This will be handled by the route protection in App.js
      return;
    }
  }, [isSuperAdmin]);

  // Fetch users from Firestore
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(usersQuery);
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        name: `${doc.data().firstName} ${doc.data().lastName}`
      }));

      // One-time lazy backfill: assign palette colors to users created before
      // the color field existed, in creation order (closest analog to the
      // PRD's "id ascending" on an auto-increment PK).
      const missingColor = usersData
        .filter(u => !u.color)
        .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
      if (missingColor.length > 0) {
        const batch = writeBatch(db);
        missingColor.forEach((u, i) => {
          u.color = defaultColorForIndex(i);
          batch.update(doc(db, 'users', u.id), { color: u.color });
        });
        try {
          await batch.commit();
        } catch (error) {
          console.error('Error backfilling user colors:', error);
        }
      }

      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Error fetching users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin()) {
      fetchUsers();
    }
  }, [isSuperAdmin]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleOpen = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'Partner',
        status: user.status || 'Active',
        password: '', // Don't populate password for editing
        color: user.color || ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'Partner',
        status: 'Active',
        password: '',
        color: defaultColorForIndex(users.length)
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'Partner',
      status: 'Active',
      password: '',
      color: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.color) {
      showSnackbar('Please select a color', 'error');
      return;
    }
    try {
      if (editingUser) {
        // Update existing user
        const userRef = doc(db, 'users', editingUser.id);
        const updateData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          status: formData.status,
          color: formData.color,
        };
        
        await updateDoc(userRef, updateData);
        showSnackbar('User updated successfully');
      } else {
        // Create new user without affecting current session using a secondary Firebase app/auth
        if (!formData.password) {
          showSnackbar('Password is required for new users', 'error');
          return;
        }

        let secondaryApp;
        try {
          // Initialize or reuse a named secondary app
          const existing = getApps().find((a) => a.name === 'Secondary');
          secondaryApp = existing || initializeApp(app.options, 'Secondary');
          const secondaryAuth = getAuth(secondaryApp);

          // Create Firebase Auth user on secondary auth instance
          const userCredential = await createUserWithEmailAndPassword(secondaryAuth, formData.email, formData.password);
          const user = userCredential.user;

          // Update display name on the new user
          await updateProfile(user, {
            displayName: `${formData.firstName} ${formData.lastName}`
          });

          // Create Firestore document in primary Firestore
          const userDocData = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            role: formData.role,
            status: formData.status,
            color: formData.color,
            createdAt: new Date().toISOString(),
            lastLogin: 'Never',
            uid: user.uid
          };

          await addDoc(collection(db, 'users'), userDocData);
          showSnackbar('User created successfully');
        } catch (createErr) {
          // If email already exists in Auth, attempt to reactivate a soft-deleted Firestore user
          if (createErr?.code === 'auth/email-already-in-use') {
            const existingUsersQuery = query(collection(db, 'users'), where('email', '==', formData.email));
            const existingSnapshot = await getDocs(existingUsersQuery);
            const existingDocs = existingSnapshot.docs;

            if (existingDocs.length > 0) {
              // Prefer reactivating a soft-deleted user document
              const softDeletedDoc = existingDocs.find(d => (d.data()?.status === 'Deleted')) || existingDocs[0];
              const userRef = doc(db, 'users', softDeletedDoc.id);

              await updateDoc(userRef, {
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                role: formData.role,
                status: 'Active',
                color: formData.color
              });

              // Send password reset so the user can set a new password
              try {
                await sendPasswordResetEmail(auth, formData.email);
              } catch (_) {
                // Ignore reset email errors to not block reactivation
              }

              showSnackbar('Existing user reactivated. Password reset email sent.');
            } else {
              // No Firestore doc but Auth account exists: offer password reset
              try {
                await sendPasswordResetEmail(auth, formData.email);
                showSnackbar('Account already exists. Password reset email sent.', 'info');
              } catch (_) {
                showSnackbar('Email address is already in use', 'error');
              }
            }
          } else {
            throw createErr;
          }
        } finally {
          // Clean up the secondary auth/app so it doesn't linger
          if (secondaryApp) {
            try { await signOut(getAuth(secondaryApp)); } catch (e) { /* noop */ }
            try { await deleteApp(secondaryApp); } catch (e) { /* noop */ }
          }
        }
      }
      
      handleClose();
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error saving user:', error);
      let errorMessage = 'Error saving user';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Email address is already in use';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }
      
      showSnackbar(errorMessage, 'error');
    }
  };

  // Firebase emails the user a link to choose a new password (setting it directly needs a server)
  const handleSendPasswordReset = async () => {
    if (!resetTarget || !isSuperAdmin()) return;
    const { user } = resetTarget;
    setResetTarget((prev) => ({ ...prev, sending: true }));
    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetTarget(null);
      showSnackbar(`Password reset email sent to ${user.email}`);
    } catch (error) {
      console.error('Error sending password reset:', error);
      setResetTarget((prev) => (prev ? { ...prev, sending: false } : prev));
      const message = error.code === 'auth/user-not-found'
        ? 'No sign-in account exists for this email'
        : error.code === 'auth/invalid-email'
          ? 'This user has an invalid email address'
          : 'Error sending password reset email';
      showSnackbar(message, 'error');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        // Note: Deleting a user from Firebase Auth requires special handling
        // For now, we'll just update the status to 'Deleted' in Firestore
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, { 
          status: 'Deleted',
          deletedAt: new Date().toISOString()
        });
        
        showSnackbar('User marked as deleted');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        showSnackbar('Error deleting user', 'error');
      }
    }
  };

  const getRoleChip = (role) => {
    const roleInfo = roleDefinitions[role];
    if (!roleInfo) return null;
    
    return (
      <Chip
        icon={roleInfo.icon}
        label={roleInfo.name}
        // color={roleInfo.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const getStatusChip = (status) => {
    const color = status === 'Active' ? 'success' : 
                  status === 'Deleted' ? 'error' : 'default';
    
    return (
      <Chip
        label={status}
        color={color}
        size="small"
      />
    );
  };

  // Don't render if not Super Admin
  if (!isSuperAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied: Only Super Administrators can access this page.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            🛠️ App Users
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            User role and permission management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add User
        </Button>
      </Box>


      {/* Role Definitions */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {Object.entries(roleDefinitions).map(([key, role]) => (
          <Grid item xs={12} md={6} key={key}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {role.icon}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {role.name}
                  </Typography>
                  <Chip
                    label={key}
                    color={role.color}
                    size="small"
                    sx={{ ml: 'auto' }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {role.description}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Permissions:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {role.permissions.map((permission, index) => (
                    <Chip
                      key={index}
                      label={permission}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Users Table */}
      <Paper sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.filter(user => user.status !== 'Deleted').map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: user.color || 'primary.main' }}>
                        {user.firstName?.charAt(0) || user.name?.charAt(0) || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.name || `${user.firstName} ${user.lastName}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {user.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">{user.email}</Typography>
                      </Box>
                      {user.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">{user.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{getRoleChip(user.role)}</TableCell>
                  <TableCell>{getStatusChip(user.status)}</TableCell>
                  <TableCell>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {user.lastLogin && user.lastLogin !== 'Never' 
                      ? new Date(user.lastLogin).toLocaleString() 
                      : user.lastLogin || 'Never'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpen(user)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <Tooltip title="Send password reset email">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => setResetTarget({ user, sending: false })}
                          disabled={!user.email}
                          sx={{ mr: 1 }}
                        >
                          <PasswordResetIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(user.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {users.filter(user => user.status !== 'Deleted').length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" autoComplete="off">
            <Box sx={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
              <input type="text" name="username" autoComplete="username" />
              <input type="password" name="password" autoComplete="new-password" />
            </Box>
            <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="First Name"
                fullWidth
                value={formData.firstName}
                name="new-user-first-name"
                autoComplete="off"
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Last Name"
                fullWidth
                value={formData.lastName}
                name="new-user-last-name"
                autoComplete="off"
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                value={formData.email}
                name="new-user-email"
                autoComplete="off"
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!editingUser} // Don't allow email changes for existing users
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phone Number"
                fullWidth
                value={formData.phone}
                name="new-user-phone"
                autoComplete="off"
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            {!editingUser && (
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  type="password"
                  fullWidth
                  value={formData.password}
                  name="new-user-password"
                  autoComplete="new-password"
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  helperText="Required for new users. Minimum 6 characters."
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <MenuItem value="SA">Super Admin (SA)</MenuItem>
                  <MenuItem value="Developer">Developer</MenuItem>
                  <MenuItem value="Partner">Partner</MenuItem>
                  <MenuItem value="Member">Member</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Color *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {USER_COLOR_PALETTE.map((hex) => {
                  const selected = formData.color === hex;
                  return (
                    <Box
                      key={hex}
                      onClick={() => setFormData({ ...formData, color: hex })}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: hex,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        outline: selected ? '2px solid' : 'none',
                        outlineColor: 'primary.main',
                        outlineOffset: '2px'
                      }}
                    >
                      {selected && <CheckIcon sx={{ color: '#fff', fontSize: 20 }} />}
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: formData.color || '#9e9e9e' }}>
                  {getInitials(`${formData.firstName} ${formData.lastName}`.trim())}
                </Avatar>
                <Typography variant="caption" color="text.secondary">
                  Preview — used for comment and task avatars
                </Typography>
              </Box>
            </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password reset confirmation */}
      <Dialog open={Boolean(resetTarget)} onClose={() => !resetTarget?.sending && setResetTarget(null)} maxWidth="xs" fullWidth>
        {resetTarget && (
          <>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Send a password reset email to{' '}
                <strong>{[resetTarget.user.firstName, resetTarget.user.lastName].filter(Boolean).join(' ') || resetTarget.user.email}</strong>{' '}
                at <strong>{resetTarget.user.email}</strong>?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                They will get a link to choose a new password. Their current password keeps working until they do.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setResetTarget(null)} disabled={resetTarget.sending}>Cancel</Button>
              <Button variant="contained" onClick={handleSendPasswordReset} disabled={resetTarget.sending}>
                {resetTarget.sending ? <CircularProgress size={22} color="inherit" /> : 'Send Reset Email'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AppUsers; 